import { Pencil, Redo2, Save, Trash2, Undo2 } from 'lucide-react';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { getAllSites } from '../SiteManagement/siteApi';
import { videoAttendanceApi } from './videoAttendanceApi';

const glassCardStyle = {
  borderRadius: '12px',
  border: '1px solid var(--card-11-stroke, #FFF)',
  background:
    'linear-gradient(0deg, rgba(255, 255, 255, 0.10) 0%, rgba(255, 255, 255, 0.10) 100%), linear-gradient(170deg, rgba(255, 255, 255, 0.08) 0%, rgba(255, 255, 255, 0.40) 97.94%)',
  boxShadow:
    '0px 5px 20.1px 0px rgba(255, 255, 255, 0.10), 0px 15px 30px 0px rgba(0, 0, 0, 0.10), 0px 4px 25px 0px rgba(255, 255, 255, 0.35) inset',
  backdropFilter: 'blur(30px)',
};

const RULE_TYPES = [
  { value: 'ppe', label: 'PPE' },
  { value: 'restricted', label: 'Restricted' },
  { value: 'restricted_ppe', label: 'Restricted + PPE' },
];

const GEAR_OPTIONS = [
  { value: 'HEAD_COVER', label: 'Head cover (helmet)' },
  { value: 'FACE_COVER', label: 'Face cover (mask)' },
  { value: 'HAND_COVER', label: 'Hand cover (gloves)' },
];

const SEVERITIES = [
  { value: 'low', label: 'Low' },
  { value: 'medium', label: 'Medium' },
  { value: 'high', label: 'High' },
];

function clamp01(n) {
  if (!Number.isFinite(n)) return 0;
  return Math.min(1, Math.max(0, n));
}

function dist2(a, b) {
  const dx = a[0] - b[0];
  const dy = a[1] - b[1];
  return dx * dx + dy * dy;
}

function clampPoint(p) {
  return [clamp01(p?.[0]), clamp01(p?.[1])];
}

// (intentionally no "edge insert" tooling here; polygon creation mirrors Site Onboarding's tool flow)

function toggleArrayValue(arr, value) {
  const has = arr.includes(value);
  if (has) return arr.filter(v => v !== value);
  return [...arr, value];
}

const DEFAULT_POLL_INTERVAL_SECONDS = 30;
const DEFAULT_FRAME_REFRESH_SECONDS = 2;

function resolveSiteValue(site) {
  if (!site || typeof site !== 'object') return '';
  const v = site.site_id ?? site.id ?? site.siteId ?? site.site_code;
  return v != null ? String(v).trim() : '';
}

function siteDisplayName(site) {
  if (!site || typeof site !== 'object') return 'Site';
  return site.site_name || site.name || site.display_name || 'Site';
}

function storageKey(cameraId) {
  const key = String(cameraId || 'default').trim() || 'default';
  return `videoAttendance.zoneDesigner.${key}`;
}

function loadFromStorage(cameraId) {
  try {
    const raw = localStorage.getItem(storageKey(cameraId));
    if (!raw) {
      return {
        zones: [],
        frameUrl: '',
        site_id: '',
        polling_enabled: false,
        poll_interval_seconds: DEFAULT_POLL_INTERVAL_SECONDS,
      };
    }
    const parsed = JSON.parse(raw);
    const pollRaw = parsed?.poll_interval_seconds;
    const pollNum = Number(pollRaw);
    return {
      zones: Array.isArray(parsed?.zones) ? parsed.zones : [],
      frameUrl: typeof parsed?.frameUrl === 'string' ? parsed.frameUrl : '',
      site_id: typeof parsed?.site_id === 'string' ? parsed.site_id : '',
      polling_enabled: Boolean(parsed?.polling_enabled),
      poll_interval_seconds:
        Number.isFinite(pollNum) && pollNum > 0 ? Math.round(pollNum) : DEFAULT_POLL_INTERVAL_SECONDS,
    };
  } catch {
    return {
      zones: [],
      frameUrl: '',
      site_id: '',
      polling_enabled: false,
      poll_interval_seconds: DEFAULT_POLL_INTERVAL_SECONDS,
    };
  }
}

function saveToStorage(cameraId, payload) {
  const {
    zones,
    frameUrl,
    site_id = '',
    polling_enabled = false,
    poll_interval_seconds = DEFAULT_POLL_INTERVAL_SECONDS,
  } = payload || {};
  const poll = Math.max(1, Math.round(Number(poll_interval_seconds) || DEFAULT_POLL_INTERVAL_SECONDS));
  localStorage.setItem(
    storageKey(cameraId),
    JSON.stringify({
      zones: Array.isArray(zones) ? zones : [],
      frameUrl: typeof frameUrl === 'string' ? frameUrl : '',
      site_id: typeof site_id === 'string' ? site_id : String(site_id || ''),
      polling_enabled: Boolean(polling_enabled),
      poll_interval_seconds: poll,
    })
  );
}

const ZoneDesignerScreen = () => {
  const location = useLocation();

  const [cameraId, setCameraId] = useState('');
  const [frameUrl, setFrameUrl] = useState('');
  const [zones, setZones] = useState([]);
  const [selectedZoneId, setSelectedZoneId] = useState('');
  const [isDrawing, setIsDrawing] = useState(false);
  const [hoverPoint, setHoverPoint] = useState(null); // normalized [x,y] while drawing
  const [previewEnabled, setPreviewEnabled] = useState(true);
  const [error, setError] = useState('');
  const [savedAt, setSavedAt] = useState('');
  const [siteId, setSiteId] = useState('');
  const [sites, setSites] = useState([]);
  const [sitesLoading, setSitesLoading] = useState(false);
  const [sitesError, setSitesError] = useState('');
  const [pollingEnabled, setPollingEnabled] = useState(false);
  const [pollIntervalSeconds, setPollIntervalSeconds] = useState(DEFAULT_POLL_INTERVAL_SECONDS);
  const [isSaving, setIsSaving] = useState(false);
  const [isLoadingRemote, setIsLoadingRemote] = useState(false);
  const [remoteInfo, setRemoteInfo] = useState('');

  const stageRef = useRef(null);
  const historyRef = useRef(new Map()); // zoneId -> { undo: polygon[], redo: polygon[] }

  const getHistory = zoneId => {
    if (!zoneId) return { undo: [], redo: [] };
    if (!historyRef.current.has(zoneId)) {
      historyRef.current.set(zoneId, { undo: [], redo: [] });
    }
    return historyRef.current.get(zoneId);
  };

  const pushHistory = (zoneId, prevPolygon) => {
    if (!zoneId) return;
    const h = getHistory(zoneId);
    const safePrev = Array.isArray(prevPolygon) ? prevPolygon.map(clampPoint) : [];
    // avoid pushing duplicates
    const last = h.undo[h.undo.length - 1];
    const same =
      Array.isArray(last) &&
      last.length === safePrev.length &&
      last.every((pt, idx) => pt[0] === safePrev[idx]?.[0] && pt[1] === safePrev[idx]?.[1]);
    if (!same) h.undo.push(safePrev);
    h.redo = [];
  };

  useEffect(() => {
    const fromState = location.state?.camera_id || location.state?.cameraId || '';
    const params = new URLSearchParams(location.search || '');
    const fromQuery = params.get('camera_id') || params.get('cameraId') || '';
    const next = String(fromState || fromQuery || '').trim();
    if (next) setCameraId(next);
    const fromSiteState = location.state?.site_id || location.state?.siteId || '';
    const fromSiteQuery = params.get('site_id') || params.get('siteId') || '';
    const nextSite = String(fromSiteState || fromSiteQuery || '').trim();
    if (nextSite) setSiteId(nextSite);
  }, [location.search, location.state]);

  useEffect(() => {
    let cancelled = false;
    setSitesLoading(true);
    setSitesError('');
    (async () => {
      try {
        const response = await getAllSites();
        const list = Array.isArray(response?.sites)
          ? response.sites
          : Array.isArray(response?.data)
            ? response.data
            : Array.isArray(response)
              ? response
              : [];
        const sorted = [...list].sort((a, b) =>
          siteDisplayName(a).localeCompare(siteDisplayName(b))
        );
        if (!cancelled) setSites(sorted);
      } catch (e) {
        if (!cancelled) {
          setSites([]);
          setSitesError(e?.message || 'Unable to load sites.');
        }
      } finally {
        if (!cancelled) setSitesLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    const {
      zones: storedZones,
      frameUrl: storedFrameUrl,
      site_id: storedSiteId,
      polling_enabled: storedPolling,
      poll_interval_seconds: storedPoll,
    } = loadFromStorage(cameraId);
    setZones(storedZones);
    setSelectedZoneId(storedZones[0]?.zone_id || '');
    setFrameUrl(storedFrameUrl);
    setSiteId(storedSiteId || '');
    setPollingEnabled(storedPolling);
    setPollIntervalSeconds(storedPoll);
  }, [cameraId]);

  // Load zones from backend (authoritative) when cameraId changes.
  useEffect(() => {
    if (!cameraId) return;
    const ac = new AbortController();
    setIsLoadingRemote(true);
    setRemoteInfo('');
    setError('');
    (async () => {
      try {
        const { zones: z } = await videoAttendanceApi.getZoneConfig({
          camera_id: cameraId,
          signal: ac.signal,
        });
        if (!ac.signal.aborted) {
          const list = Array.isArray(z) ? z : [];
          setZones(list);
          setSelectedZoneId(list[0]?.zone_id || '');
          setRemoteInfo('Loaded zones from backend.');
        }
      } catch (e) {
        if (!ac.signal.aborted) {
          setRemoteInfo('');
          setError(e?.message || 'Failed to load zone config from backend.');
        }
      } finally {
        if (!ac.signal.aborted) setIsLoadingRemote(false);
      }
    })();
    return () => ac.abort();
  }, [cameraId]);

  // Fetch a fresh background frame once per camera change.
  useEffect(() => {
    if (!cameraId) return;
    const ac = new AbortController();
    refreshFrameUrl(ac.signal);
    return () => ac.abort();
  }, [cameraId]);

  const refreshFrameUrl = async signal => {
    if (!cameraId) return;
    try {
      const { url } = await videoAttendanceApi.getFrameUrl({ camera_id: cameraId, signal });
      if (!signal?.aborted && typeof url === 'string' && url) {
        setFrameUrl(url);
      }
    } catch (e) {
      if (!signal?.aborted) setError(e?.message || 'Failed to load frame URL.');
    }
  };

  // Optional polling for background frame (useful for designing on the latest image).
  useEffect(() => {
    if (!cameraId) return;
    if (!pollingEnabled) return;
    const ac = new AbortController();
    refreshFrameUrl(ac.signal);
    const seconds = Math.max(1, Math.round(Number(pollIntervalSeconds) || DEFAULT_FRAME_REFRESH_SECONDS));
    const t = window.setInterval(() => refreshFrameUrl(ac.signal), seconds * 1000);
    return () => {
      ac.abort();
      window.clearInterval(t);
    };
  }, [cameraId, pollingEnabled, pollIntervalSeconds]);

  const selectedZone = useMemo(
    () => zones.find(z => z.zone_id === selectedZoneId) || null,
    [zones, selectedZoneId]
  );

  const isPolygonClosed = useMemo(() => {
    if (!selectedZone) return false;
    const poly = Array.isArray(selectedZone.polygon) ? selectedZone.polygon : [];
    return !isDrawing && poly.length >= 3;
  }, [selectedZone, isDrawing]);

  const deleteSelectedZone = () => {
    if (!selectedZoneId) return;
    setZones(prev => prev.filter(z => z.zone_id !== selectedZoneId));
    setSelectedZoneId('');
    setIsDrawing(false);
    setHoverPoint(null);
  };

  const updateSelectedZone = patch => {
    if (!selectedZoneId) return;
    setZones(prev => {
      const current = prev.find(z => z.zone_id === selectedZoneId) || null;
      if (current && Object.prototype.hasOwnProperty.call(patch || {}, 'polygon')) {
        pushHistory(selectedZoneId, Array.isArray(current.polygon) ? current.polygon : []);
      }
      return prev.map(z => (z.zone_id === selectedZoneId ? { ...z, ...patch } : z));
    });
  };

  const getPointerNorm = evt => {
    const stage = stageRef.current;
    if (!stage) return null;
    const rect = stage.getBoundingClientRect();
    const x = (evt.clientX - rect.left) / rect.width;
    const y = (evt.clientY - rect.top) / rect.height;
    return [clamp01(x), clamp01(y)];
  };

  const tryClosePolygon = (poly, p, thresholdNorm) => {
    if (poly.length < 3) return false;
    const first = poly[0];
    return dist2(first, p) <= thresholdNorm * thresholdNorm;
  };

  const onStageClick = evt => {
    if (!selectedZone) return;
    if (!isDrawing) return;
    const p = getPointerNorm(evt);
    if (!p) return;

    const poly = Array.isArray(selectedZone.polygon) ? selectedZone.polygon : [];
    // If user clicks an existing vertex, disable further adding.
    // If it's the first vertex AND we have enough points, treat it as "close".
    const hitIndex = findVertexHit(selectedZone, p);
    if (hitIndex >= 0) {
      if (hitIndex === 0 && poly.length >= 3) {
        setIsDrawing(false);
        setHoverPoint(null);
      } else {
        // clicked on some existing vertex => stop drawing (disable)
        setIsDrawing(false);
        setHoverPoint(null);
      }
      return;
    }

    const closeThreshold = 0.02; // ~2% of image size
    if (tryClosePolygon(poly, p, closeThreshold)) {
      setIsDrawing(false);
      setHoverPoint(null);
      return;
    }
    updateSelectedZone({ polygon: [...poly, p] });
  };

  const onStageDoubleClick = () => {
    if (!isDrawing) return;
    // Google Maps polygon tool ends drawing explicitly; here double-click ends as well.
    setIsDrawing(false);
    setHoverPoint(null);
  };

  const findVertexHit = (zone, p) => {
    const poly = Array.isArray(zone?.polygon) ? zone.polygon : [];
    if (!poly.length) return -1;
    const threshold = 0.02;
    const t2 = threshold * threshold;
    for (let i = 0; i < poly.length; i += 1) {
      if (dist2(poly[i], p) <= t2) return i;
    }
    return -1;
  };

  const onPointerDown = () => {};

  const onPointerMove = evt => {
    if (!isDrawing) return;
    if (!selectedZone) return;
    const p = getPointerNorm(evt);
    if (!p) return;
    setHoverPoint(p);
  };

  const onMouseMove = evt => {
    // Fallback for environments where pointer events are flaky.
    onPointerMove(evt);
  };

  const onPointerUp = () => {};

  const canUndo = useMemo(() => {
    if (!selectedZoneId) return false;
    const h = getHistory(selectedZoneId);
    return Array.isArray(h.undo) && h.undo.length > 0;
  }, [selectedZoneId, zones]);

  const canRedo = useMemo(() => {
    if (!selectedZoneId) return false;
    const h = getHistory(selectedZoneId);
    return Array.isArray(h.redo) && h.redo.length > 0;
  }, [selectedZoneId, zones]);

  const undoPolygon = () => {
    if (!selectedZone) return;
    const h = getHistory(selectedZone.zone_id);
    if (!h.undo.length) return;
    const currentPoly = Array.isArray(selectedZone.polygon) ? selectedZone.polygon.map(clampPoint) : [];
    const prevPoly = h.undo.pop();
    h.redo.push(currentPoly);
    setZones(prev => prev.map(z => (z.zone_id === selectedZone.zone_id ? { ...z, polygon: prevPoly } : z)));
  };

  const redoPolygon = () => {
    if (!selectedZone) return;
    const h = getHistory(selectedZone.zone_id);
    if (!h.redo.length) return;
    const currentPoly = Array.isArray(selectedZone.polygon) ? selectedZone.polygon.map(clampPoint) : [];
    const nextPoly = h.redo.pop();
    h.undo.push(currentPoly);
    setZones(prev => prev.map(z => (z.zone_id === selectedZone.zone_id ? { ...z, polygon: nextPoly } : z)));
  };

  const save = async () => {
    setError('');
    setRemoteInfo('');
    if (!cameraId) {
      setError('Camera ID is required to save zone config.');
      return;
    }
    setIsSaving(true);
    try {
      // Save to backend (authoritative)
      const response = await videoAttendanceApi.putZoneConfig({
        camera_id: cameraId,
        zoneConfig: { zones: Array.isArray(zones) ? zones : [] },
      });

      // Also keep local backup for quick recovery
      try {
        saveToStorage(cameraId, {
          zones,
          frameUrl,
          site_id: siteId,
          polling_enabled: pollingEnabled,
          poll_interval_seconds: pollIntervalSeconds,
        });
      } catch {
        // ignore local backup failures
      }

      const updatedAt =
        typeof response?.updated_at === 'string'
          ? response.updated_at
          : typeof response?.updatedAt === 'string'
            ? response.updatedAt
            : '';
      setSavedAt(updatedAt ? new Date(updatedAt).toLocaleString() : new Date().toLocaleString());
      setRemoteInfo('Saved to backend.');
    } catch (e) {
      setError(e?.message || 'Failed to save to backend.');
    } finally {
      setIsSaving(false);
    }
  };

  const togglePolygonDrawing = () => {
    if (!selectedZoneId) return;
    if (isPolygonClosed) return;
    if (!isDrawing) {
      updateSelectedZone({ polygon: [] });
      setIsDrawing(true);
      setHoverPoint(null);
    } else {
      setIsDrawing(false);
      setHoverPoint(null);
    }
  };

  return (
    <div className="w-full h-full min-h-screen box-border flex flex-col bg-gradient-to-br from-blue-50 to-indigo-100 p-3 sm:p-6">
      <div className="flex flex-1 flex-col lg:flex-row gap-4 min-h-0">
          {/* Left: Camera controls — full viewport height on large screens */}
          <aside
            className="w-full lg:w-[360px] shrink-0 rounded-2xl p-3 sm:p-4 flex flex-col gap-3 border border-white/70 bg-white/60 backdrop-blur-xl shadow-sm lg:h-full lg:min-h-0 lg:max-h-screen overflow-hidden"
            style={glassCardStyle}
          >
            <div className="flex items-center justify-between gap-2 pb-2 border-b border-white/40 shrink-0">
              <div>
                <div className="text-sm font-semibold text-neutral-900">Camera</div>
                <div className="text-xs text-neutral-600">View camera</div>
              </div>
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={togglePolygonDrawing}
                  disabled={!selectedZoneId || isPolygonClosed}
                  className={`inline-flex items-center justify-center w-9 h-9 rounded-xl border shadow-sm disabled:opacity-60 ${
                    isDrawing
                      ? 'border-rose-200 bg-rose-50 hover:bg-rose-100'
                      : 'border-white/70 bg-white/70 hover:bg-white'
                  }`}
                  title={isDrawing ? 'Finish editing polygon' : 'Edit zone polygon'}
                >
                  <Pencil className="w-4 h-4 text-neutral-800" />
                </button>
                <button
                  type="button"
                  onClick={deleteSelectedZone}
                  disabled={!selectedZoneId}
                  className="inline-flex items-center justify-center w-9 h-9 rounded-xl border border-rose-200 bg-rose-50 hover:bg-rose-100 disabled:opacity-60"
                  title="Delete selected zone"
                >
                  <Trash2 className="w-4 h-4 text-rose-700" />
                </button>
              </div>
            </div>

            <div className="space-y-3 flex-1 min-h-0 overflow-y-auto">
              <div>
                <label className="block text-sm font-semibold text-neutral-900 mb-1">Camera</label>
                <input
                  value={cameraId}
                  onChange={e => setCameraId(e.target.value)}
                  placeholder="Enter the camera identity"
                  className="w-full rounded-xl border border-neutral-200 bg-white px-3 py-2 text-sm text-neutral-900 shadow-sm focus:outline-none focus:ring-2 focus:ring-sky-200"
                />
              </div>

              <div>
                <label className="block text-sm font-semibold text-neutral-900 mb-1">Site</label>
                <select
                  value={siteId}
                  onChange={e => setSiteId(e.target.value)}
                  disabled={sitesLoading}
                  className="w-full rounded-xl border border-neutral-200 bg-white px-3 py-2 text-sm text-neutral-900 shadow-sm focus:outline-none focus:ring-2 focus:ring-sky-200 disabled:opacity-70"
                >
                  <option value="">{sitesLoading ? 'Loading sites…' : 'Select a site'}</option>
                  {sites.map(site => {
                    const id = resolveSiteValue(site);
                    if (!id) return null;
                    return (
                      <option key={id} value={id}>
                        {siteDisplayName(site)}
                      </option>
                    );
                  })}
                </select>
                {sitesError ? <p className="mt-1 text-xs text-rose-600">{sitesError}</p> : null}
              </div>

              <div>
                <label className="flex items-center justify-between gap-2 rounded-xl border border-neutral-200 bg-white px-3 py-2 text-sm text-neutral-900 shadow-sm select-none">
                  <span className="font-semibold">Polling enabled</span>
                  <input
                    type="checkbox"
                    className="h-5 w-5 rounded border-neutral-300 accent-sky-600"
                    checked={pollingEnabled}
                    onChange={e => setPollingEnabled(e.target.checked)}
                  />
                </label>
              </div>

              {pollingEnabled ? (
                <div>
                  <label className="block text-sm font-semibold text-neutral-900 mb-1">
                    Poll interval (seconds)
                  </label>
                  <input
                    type="number"
                    min={1}
                    step={1}
                    value={pollIntervalSeconds}
                    onChange={e => {
                      const n = Math.max(1, Math.round(Number(e.target.value) || DEFAULT_POLL_INTERVAL_SECONDS));
                      setPollIntervalSeconds(n);
                    }}
                    className="w-full rounded-xl border border-neutral-200 bg-white px-3 py-2 text-sm text-neutral-900 shadow-sm focus:outline-none focus:ring-2 focus:ring-sky-200"
                  />
                </div>
              ) : null}

              <div>
                <label className="block text-sm font-semibold text-neutral-900 mb-1">Create Zones</label>
                <select
                  value={selectedZoneId}
                  onChange={e => {
                    setSelectedZoneId(e.target.value);
                    setIsDrawing(false);
                  }}
                  className="w-full rounded-xl border border-neutral-200 bg-white px-3 py-2 text-sm text-neutral-900 shadow-sm focus:outline-none focus:ring-2 focus:ring-sky-200"
                >
                  <option value="" disabled>
                    Select a zone…
                  </option>
                  {zones.map((z, idx) => (
                    <option key={z.zone_id} value={z.zone_id}>
                      {z.zone_label || `Zone ${idx + 1}`}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-semibold text-neutral-900 mb-1">Zone</label>
                <input
                  value={selectedZone?.zone_label || ''}
                  onChange={e => updateSelectedZone({ zone_label: e.target.value })}
                  placeholder="Enter the zone identity"
                  disabled={!selectedZoneId}
                  className="w-full rounded-xl border border-neutral-200 bg-white px-3 py-2 text-sm text-neutral-900 shadow-sm disabled:opacity-70 focus:outline-none focus:ring-2 focus:ring-sky-200"
                />
              </div>

              <div>
                <label className="block text-sm font-semibold text-neutral-900 mb-1">Zone type</label>
                <select
                  value={selectedZone?.rule_type || 'restricted_ppe'}
                  onChange={e => updateSelectedZone({ rule_type: e.target.value })}
                  disabled={!selectedZoneId}
                  className="w-full rounded-xl border border-neutral-200 bg-white px-3 py-2 text-sm text-neutral-900 shadow-sm disabled:opacity-70 focus:outline-none focus:ring-2 focus:ring-sky-200"
                >
                  {RULE_TYPES.map(opt => (
                    <option key={opt.value} value={opt.value}>
                      {opt.label}
                    </option>
                  ))}
                </select>
              </div>

              <div className="pt-1">
                <div className="text-sm font-semibold text-neutral-900 mb-2">Required Gear Safety Check</div>
                <div className="flex flex-col gap-2">
                  {GEAR_OPTIONS.map(opt => {
                    const checked = Array.isArray(selectedZone?.required_gear)
                      ? selectedZone.required_gear.includes(opt.value)
                      : false;
                    const label =
                      opt.value === 'HEAD_COVER'
                        ? 'Helmet'
                        : opt.value === 'FACE_COVER'
                          ? 'Safety Vest'
                          : 'Gloves';
                    return (
                      <label
                        key={opt.value}
                        className="flex items-center justify-between gap-2 rounded-xl border border-neutral-200 bg-white px-3 py-2 text-sm text-neutral-900 shadow-sm select-none"
                      >
                        <span className="font-medium">{label}</span>
                        <input
                          type="checkbox"
                          className="h-5 w-5 rounded border-neutral-300 accent-emerald-600"
                          checked={checked}
                          disabled={!selectedZoneId}
                          onChange={() =>
                            updateSelectedZone({
                              required_gear: toggleArrayValue(
                                Array.isArray(selectedZone?.required_gear) ? selectedZone.required_gear : [],
                                opt.value
                              ),
                            })
                          }
                        />
                      </label>
                    );
                  })}
                </div>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm font-semibold text-neutral-900 mb-1">severity</label>
                  <select
                    value={selectedZone?.severity || 'medium'}
                    onChange={e => updateSelectedZone({ severity: e.target.value })}
                    disabled={!selectedZoneId}
                    className="w-full rounded-xl border border-neutral-200 bg-white px-3 py-2 text-sm text-neutral-900 shadow-sm disabled:opacity-70 focus:outline-none focus:ring-2 focus:ring-sky-200"
                  >
                    {SEVERITIES.map(opt => (
                      <option key={opt.value} value={opt.value}>
                        {opt.label}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-semibold text-neutral-900 mb-1">cool down(ms)</label>
                  <input
                    type="number"
                    min={0}
                    value={
                      Number.isFinite(selectedZone?.cooldown_seconds)
                        ? Math.round((selectedZone.cooldown_seconds || 0) * 1000)
                        : 0
                    }
                    onChange={e =>
                      updateSelectedZone({
                        cooldown_seconds: Math.max(0, Math.round(Number(e.target.value || 0) / 1000)),
                      })
                    }
                    disabled={!selectedZoneId}
                    className="w-full rounded-xl border border-neutral-200 bg-white px-3 py-2 text-sm text-neutral-900 shadow-sm disabled:opacity-70 focus:outline-none focus:ring-2 focus:ring-sky-200"
                  />
                </div>
              </div>

              <div className="rounded-xl border border-neutral-200 bg-white px-3 py-2 shadow-sm">
                <div className="text-xs text-neutral-600">Polygon</div>
                <div className="text-sm font-semibold text-neutral-900">
                  Vertices: {Array.isArray(selectedZone?.polygon) ? selectedZone.polygon.length : 0}
                </div>
                <div className="mt-2 flex items-center gap-2">
                  <button
                    type="button"
                    onClick={togglePolygonDrawing}
                    disabled={!selectedZoneId || isPolygonClosed}
                    className={`px-3 py-2 rounded-xl text-xs font-semibold border shadow-sm ${
                      isDrawing
                        ? 'bg-rose-600 text-white !text-white border-rose-700 hover:bg-rose-700'
                        : 'bg-white text-neutral-900 border-neutral-200 hover:bg-neutral-50'
                    } disabled:opacity-60`}
                  >
                    {isDrawing ? 'Finish polygon' : 'Polygon tool'}
                  </button>
                  <button
                    type="button"
                    onClick={() => updateSelectedZone({ polygon: [] })}
                    disabled={!selectedZoneId}
                    className="px-3 py-2 rounded-xl text-xs font-semibold border border-neutral-200 bg-white hover:bg-neutral-50 shadow-sm disabled:opacity-60"
                  >
                    Clear polygon
                  </button>
                </div>
              </div>

              {remoteInfo ? (
                <div className="rounded-xl border border-emerald-200 bg-emerald-50 px-3 py-2 text-sm text-emerald-800">
                  {remoteInfo}
                </div>
              ) : null}

              {isLoadingRemote ? (
                <div className="rounded-xl border border-sky-200 bg-sky-50 px-3 py-2 text-sm text-sky-800">
                  Loading zones from backend…
                </div>
              ) : null}

              {error ? (
                <div className="rounded-xl border border-rose-200 bg-rose-50 px-3 py-2 text-sm text-rose-800">
                  {error}
                </div>
              ) : null}

              <div className="flex items-center justify-between gap-2 pt-1">
                <label className="inline-flex items-center gap-2 text-sm text-neutral-700 select-none">
                  <input
                    type="checkbox"
                    className="h-4 w-4 rounded border-neutral-300 accent-sky-600"
                    checked={previewEnabled}
                    onChange={e => setPreviewEnabled(e.target.checked)}
                  />
                  Preview
                </label>
                <button
                  type="button"
                  onClick={save}
                  disabled={isSaving}
                  className="inline-flex items-center gap-2 rounded-xl bg-sky-600 text-white !text-white px-4 py-2 text-sm font-semibold hover:bg-sky-700 shadow-sm"
                >
                  <Save className="w-4 h-4 text-white" />
                  {isSaving ? 'Saving…' : 'Save'}
                </button>
              </div>

              {savedAt ? (
                <div className="text-xs text-neutral-600">
                  Last saved: <span className="font-semibold text-neutral-800">{savedAt}</span>
                </div>
              ) : null}
            </div>
          </aside>

          {/* Right: video + canvas */}
          <section
            className="flex-1 min-w-0 min-h-0 flex flex-col rounded-2xl border border-white/70 bg-white/60 backdrop-blur-xl shadow-sm lg:h-full lg:min-h-0 lg:max-h-screen"
            style={glassCardStyle}
          >
            <div className="flex items-center justify-between gap-2 px-3 sm:px-4 py-3 border-b border-white/40 shrink-0">
              <div className="text-sm font-semibold text-neutral-900">
                {selectedZone?.zone_label ? selectedZone.zone_label : selectedZoneId ? 'Zone' : 'Zone - 1'}
              </div>
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={undoPolygon}
                  disabled={!selectedZoneId || !canUndo}
                  className="inline-flex items-center justify-center w-10 h-10 rounded-xl border border-neutral-200 bg-white hover:bg-neutral-50 disabled:opacity-60"
                  title="Undo"
                >
                  <Undo2 className="w-4 h-4 text-neutral-800" />
                </button>
                <button
                  type="button"
                  onClick={redoPolygon}
                  disabled={!selectedZoneId || !canRedo}
                  className="inline-flex items-center justify-center w-10 h-10 rounded-xl border border-neutral-200 bg-white hover:bg-neutral-50 disabled:opacity-60"
                  title="Redo"
                >
                  <Redo2 className="w-4 h-4 text-neutral-800" />
                </button>
              </div>
            </div>

            <div className="flex flex-1 min-h-0 flex-col overflow-hidden">
              <div className="relative flex-1 min-h-0 w-full bg-black/80 overflow-hidden">
                <div
                  ref={stageRef}
                  className={`absolute inset-0 w-full h-full select-none touch-none ${isDrawing ? 'cursor-crosshair' : 'cursor-default'}`}
                  onClick={onStageClick}
                  onDoubleClick={onStageDoubleClick}
                  onPointerDown={onPointerDown}
                  onPointerMove={onPointerMove}
                  onMouseMove={onMouseMove}
                  onPointerUp={onPointerUp}
                  onPointerCancel={onPointerUp}
                  onPointerLeave={() => {
                    onPointerUp();
                    setHoverPoint(null);
                  }}
                  onContextMenu={e => e.preventDefault()}
                  role="application"
                  aria-label="Zone polygon editor"
                >
                  {frameUrl ? (
                    <img
                      src={frameUrl}
                      alt="Latest frame"
                      className="absolute inset-0 w-full h-full object-cover"
                      onError={() => setError('Failed to load frame image (check the URL).')}
                    />
                  ) : (
                    <div className="absolute inset-0 flex items-center justify-center">
                      <p className="text-sm text-white/80">No frame URL set.</p>
                    </div>
                  )}

                  {previewEnabled ? (
                    <svg
                      className="absolute inset-0 w-full h-full pointer-events-none"
                      viewBox="0 0 1 1"
                      preserveAspectRatio="none"
                    >
                      {zones.map(zone => {
                        const poly = Array.isArray(zone.polygon) ? zone.polygon : [];
                        const points = poly.map(p => `${p[0]},${p[1]}`).join(' ');
                        const isSelected = zone.zone_id === selectedZoneId;
                        const stroke = isSelected ? '#38bdf8' : '#e5e7eb';
                        const fill = isSelected ? 'rgba(56,189,248,0.15)' : 'rgba(255,255,255,0.08)';
                        const isSelectedAndDrawing = isSelected && isDrawing;
                        const lastSegPoints =
                          isSelectedAndDrawing && hoverPoint && poly.length
                            ? `${poly[poly.length - 1][0]},${poly[poly.length - 1][1]} ${hoverPoint[0]},${hoverPoint[1]}`
                            : '';

                        return (
                          <g key={zone.zone_id}>
                            {poly.length >= 3 && !(isSelected && isDrawing) ? (
                              <polygon
                                points={points}
                                fill={fill}
                                stroke={stroke}
                                strokeWidth={0.004}
                                strokeLinejoin="round"
                              />
                            ) : poly.length >= 2 ? (
                              <polyline
                                points={points}
                                fill="transparent"
                                stroke={stroke}
                                strokeWidth={0.004}
                                strokeLinejoin="round"
                              />
                            ) : null}
                            {lastSegPoints ? (
                              <polyline
                                points={lastSegPoints}
                                fill="transparent"
                                stroke="#fbbf24"
                                strokeOpacity={0.95}
                                strokeWidth={0.006}
                                strokeLinejoin="round"
                              />
                            ) : null}
                            {isSelected
                              ? poly.map((p, idx) => (
                                  <circle
                                    key={`${zone.zone_id}-${idx}`}
                                    cx={p[0]}
                                    cy={p[1]}
                                    r={0.01}
                                    fill="#ffffff"
                                    stroke="#0ea5e9"
                                    strokeWidth={0.004}
                                  />
                                ))
                              : null}
                          </g>
                        );
                      })}
                    </svg>
                  ) : null}
                </div>
              </div>

              <div className="shrink-0 px-3 sm:px-4 py-2 text-xs text-neutral-700">
                {isDrawing ? (
                  <p>Polygon tool: click to add vertices. Click near the first vertex (or double-click) to finish.</p>
                ) : (
                  <p>Tip: enable “Polygon tool” to draw a new polygon.</p>
                )}
                {isPolygonClosed ? (
                  <p className="mt-1 text-[11px] text-neutral-600">Polygon is closed. Clear polygon to draw again.</p>
                ) : null}
              </div>

              {/* Hidden but preserved: ability to change frame URL without cluttering UI */}
              <div className="sr-only" aria-hidden="true">
                <input value={frameUrl} onChange={e => setFrameUrl(e.target.value)} />
              </div>
            </div>
          </section>
      </div>
    </div>
  );
};

export default ZoneDesignerScreen;
